import React from 'react';
import { Link } from 'react-router-dom';
import I3Modal from './I3Modal';
import { useI3Modal, I3ModalId } from '../context/I3ModalContext';

const SERVICES_BASE = '/services';
const CONTACT = '/contact';

function ModalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="i3-modal-section" aria-label={title}>
      <h3 className="i3-modal-section-title">{title}</h3>
      {children}
    </section>
  );
}

const modalContents: Record<Exclude<I3ModalId, null>, { title: string; body: React.ReactNode }> = {
  marriage: {
    title: 'Marriage enrichment',
    body: (
      <>
        <p className="i3-modal-lead">
          Guided coaching and retreats for couples who want a stronger partnership—rooted in communication,
          covenant, and practical tools you can use every day.
        </p>
        <ModalSection title="What we offer">
          <ul className="i3-modal-list">
            <li>Private or joint coaching sessions (by arrangement)</li>
            <li>Marriage retreats and intensive weekends (e.g. Fanning the Flame)</li>
            <li>Pre-marital preparation and foundation-building conversations</li>
          </ul>
        </ModalSection>
        <ModalSection title="You can expect">
          <ul className="i3-modal-list">
            <li>Clearer communication and healthier conflict patterns</li>
            <li>A shared vision for your marriage and family</li>
            <li>Faith-centered perspective with actionable next steps</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={SERVICES_BASE} className="i3-cta i3-cta-primary">
            Services, book &amp; pay
          </Link>
          <Link to={CONTACT} className="i3-cta i3-cta-secondary">
            Ask a question
          </Link>
        </div>
      </>
    ),
  },
  leadership: {
    title: 'Leadership development',
    body: (
      <>
        <p className="i3-modal-lead">
          One-on-one and group coaching for leaders who want to grow in influence, integrity, and clarity—at
          home, at work, and in the community.
        </p>
        <ModalSection title="What we offer">
          <ul className="i3-modal-list">
            <li>Individual leadership coaching (goals, decisions, team dynamics)</li>
            <li>Group or cohort-style sessions when scheduled</li>
            <li>Integration of faith, character, and practical leadership skills</li>
          </ul>
        </ModalSection>
        <ModalSection title="You can expect">
          <ul className="i3-modal-list">
            <li>Stronger sense of purpose and direction</li>
            <li>Tools for communication, boundaries, and healthy teams</li>
            <li>Confidence to lead in line with your values</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={SERVICES_BASE} className="i3-cta i3-cta-primary">
            Services, book &amp; pay
          </Link>
          <Link to={CONTACT} className="i3-cta i3-cta-secondary">
            Ask a question
          </Link>
        </div>
      </>
    ),
  },
  financial: {
    title: 'Financial wellness',
    body: (
      <>
        <p className="i3-modal-lead">
          Workshops and coaching to help you build a realistic plan, reduce money stress, and align your
          finances with your values—using proven financial wellness frameworks.
        </p>
        <ModalSection title="What we offer">
          <ul className="i3-modal-list">
            <li>Financial wellness workshops (group learning)</li>
            <li>One-on-one sessions tailored to your situation</li>
            <li>Budgeting, planning, and healthy money habits you can sustain</li>
          </ul>
        </ModalSection>
        <ModalSection title="You can expect">
          <ul className="i3-modal-list">
            <li>A clearer picture of income, expenses, and goals</li>
            <li>Less anxiety and more confidence around money decisions</li>
            <li>Concrete steps toward long-term financial health</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={SERVICES_BASE} className="i3-cta i3-cta-primary">
            Services, book &amp; pay
          </Link>
          <Link to={CONTACT} className="i3-cta i3-cta-secondary">
            Ask a question
          </Link>
        </div>
      </>
    ),
  },
  speaking: {
    title: 'Speaking engagements',
    body: (
      <>
        <p className="i3-modal-lead">
          Invite I-3 to your retreat, conference, staff day, or community gathering—keynotes, panels, and
          workshops on marriage, leadership, and purpose, tailored to your audience and time slot.
        </p>
        <ModalSection title="What we offer">
          <ul className="i3-modal-list">
            <li>Faith-centered keynotes and breakout sessions</li>
            <li>Panels and workshops (length and format to fit your event)</li>
            <li>Collaboration on theme, audience, and practical takeaways</li>
          </ul>
        </ModalSection>
        <ModalSection title="Good next step">
          <ul className="i3-modal-list">
            <li>Share your date, venue, and audience—we&apos;ll follow up with availability</li>
            <li>See the full speaking offering on Services</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to={CONTACT} className="i3-cta i3-cta-primary">
            Contact us
          </Link>
          <Link to={SERVICES_BASE} className="i3-cta i3-cta-secondary">
            View on Services
          </Link>
        </div>
      </>
    ),
  },
  founders: {
    title: 'Meet the founders',
    body: (
      <>
        <p className="i3-modal-lead">
          Troy and Sharrea bring decades of leadership, ministry, and life experience to the couples,
          individuals, and leaders they serve. Their journey and calling led to I-3 Coaching &amp; Consulting.
        </p>
        <ModalSection title="Why I-3">
          <ul className="i3-modal-list">
            <li>Impact, Influence, Inspire—the heart of how they show up</li>
            <li>Commitment to purpose, healing, and growth in Christ</li>
            <li>Real-world experience in ministry, corporate leadership, and family life</li>
          </ul>
        </ModalSection>
        <div className="i3-modal-actions">
          <Link to="/about" className="i3-cta i3-cta-primary">
            Read full story
          </Link>
          <Link to={CONTACT} className="i3-cta i3-cta-secondary">
            Get in touch
          </Link>
        </div>
      </>
    ),
  },
};

const I3ModalContent: React.FC = () => {
  const { modalId } = useI3Modal();

  if (!modalId) return null;

  const { title, body } = modalContents[modalId];

  return (
    <I3Modal id={modalId} title={title}>
      {body}
    </I3Modal>
  );
};

export default I3ModalContent;
